import { useEffect, useRef, useState } from "react"
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView"
import { CommentSectionProps } from "./CommentsSection.types"
import AddCommentIcon from "../../../../media/AddCommentIcon"
import { MAX_COMMENT_CHARACTERS } from "../../../../utils/constants"
import UserComment from "../../../../components/UserComment/UserComment"
import { IsolatedScanComment } from "../../../../types/types"
import ErrorIcon from "../../../../media/ErrorIcon"
import "./CommentsSection.css"

const CommentsSection = (props: CommentSectionProps) => {

  const {
    title,
    comments,
    onLoad,
    onAdd,
    onDelete
  } = props

  const [newComment, setComment] = useState("")
  const [commentValidationError, setCommentValidationError] = useState<string>("")
  const commentsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchComments = async () => {
      await onLoad()
    }
    fetchComments()
  }, [title])

  useEffect(() => {
    if (commentsRef.current) {
      commentsRef.current.scrollTop = commentsRef.current.scrollHeight;
    }
    setCommentValidationError("")
  }, [comments])

  const addScanComment = () => {
    if (!validateComment()) { return }
    onAdd(newComment)
    setComment("")
  }

  const deleteComment = (comment: IsolatedScanComment) => {
    onDelete(comment)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      addScanComment()
    }
  }

  const validateComment = () => {
    if (newComment.length === 0) {
      setCommentValidationError("Cannot submit empty comment.")
      return false
    }

    if (newComment.length > MAX_COMMENT_CHARACTERS) {
      setCommentValidationError(`Cannot submit comment with over ${MAX_COMMENT_CHARACTERS} number of characters`)
      return false
    }
    setCommentValidationError("")
    return true
  }

  if (comments.status === "error") {
    return (
      <div className="analysis-section comment-error-section">
        <ErrorIcon />
        <p>Failed to load comments.</p>
      </div>
    )
  }

  if (comments.status === "loading") {
    return (
      <div className="analysis-section">
        <SkeletonLoadingView style={{height: 20}} />
        <br />
        <SkeletonLoadingView style={{height: 120}} />
      </div>
    )
  }

  if (comments.status === "idle") { return <></>}

  return (
    <div className="analysis-section isolated-scan-comments-container">

      {comments.data !== null && comments.data.length > 0 ? (
        <div className="comments-container" ref={commentsRef}>
          {comments.data.map((comment, _) => (
            <UserComment
              key={comment.id}
              username={comment.user.username}
              comment={comment.comment}
              onDelete={() => deleteComment(comment)}
            />
          ))}
        </div>
      ) : (
        <div className="no-comments-container">
          <p>No comments saved.</p>
        </div>
      )}

      <div>
        <form className="isolated-scan-comment-text-container" onSubmit={addScanComment}>
          <div 
            className="add-comment-button"
            style={newComment === "" ? {opacity: 0.4, cursor: "default"}: {opacity: 1, cursor: "pointer"}}
            onClick={newComment === "" ? () => {} : () => addScanComment()}
          >
            <AddCommentIcon />
          </div>
          <textarea
            className="isolated-scan-comment-text"
            value={newComment}
            onKeyDown={onKeyDown}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment"
          />
        </form>
        <p className="comment-error">{commentValidationError}</p>
      </div>
    </div>
  )
}

export default CommentsSection